import { Injectable, signal } from '@angular/core';
import { environment } from '@environment';
import { Spin } from '@shared/types/spin';
import { Wheel } from '@longnecktech/splash-commons-fe';
import { catchError, defer, from, map, tap, throwError } from 'rxjs';
import { luckyDaysTheme } from '../wheel-store/luckydays-theme';
import { EngineAuthenticationError, EngineClient } from './engine-client';
import { engineWinningSegment, mapEngineWheel } from './engine.adapter';
import { EngineSpinState } from './engine.models';

@Injectable({ providedIn: 'root' })
export class EngineService {
  readonly error = signal('');
  readonly loginRequired = signal(false);
  readonly signingIn = signal(false);
  readonly acceptsLaunchToken = environment.name === 'local';
  readonly spinState = signal<EngineSpinState | null>(null);
  private game?: Wheel;

  loginUrl(): string {
    return this.client().loginUrl(window.location.href);
  }

  async signInWithToken(token: string): Promise<void> {
    if (this.signingIn()) return;
    this.signingIn.set(true);
    try {
      const gameId =
        new URLSearchParams(window.location.search).get('gameId') ?? '';
      await this.client().authenticate(gameId, token);
      window.location.reload();
    } catch (error) {
      this.setError(error);
    } finally {
      this.signingIn.set(false);
    }
  }

  setError(error: unknown): void {
    this.loginRequired.set(error instanceof EngineAuthenticationError);
    this.error.set(
      error instanceof Error ? error.message : 'Unable to load the wheel.',
    );
  }

  private client(): EngineClient {
    if (!environment.engineApiUrl)
      throw new Error(
        'The board engine is not configured in this environment.',
      );
    const params = new URLSearchParams(window.location.search);
    const timezone =
      params.get('timezone') ||
      Intl.DateTimeFormat().resolvedOptions().timeZone;
    new Intl.DateTimeFormat('en', { timeZone: timezone });
    return new EngineClient(
      new URL(environment.engineApiUrl, window.location.origin).href,
      timezone,
    );
  }

  load() {
    return defer(() => {
      const url = new URL(window.location.href);
      const gameId = url.searchParams.get('gameId') ?? '';
      const token = url.searchParams.get('token')?.trim() || undefined;
      if (url.searchParams.has('token')) {
        url.searchParams.delete('token');
        window.history.replaceState(window.history.state, '', url.href);
      }
      return from(this.client().load(gameId, token));
    }).pipe(
      map(({ response, config, labels }) => {
        if (!config.theme && config.themeKey !== 'luckydays')
          throw new Error('This wheel theme is not available yet.');
        this.spinState.set(response.startPosition);
        this.game = mapEngineWheel(
          config,
          config.theme ?? luckyDaysTheme,
          labels,
        );
        return this.game;
      }),
    );
  }

  play() {
    return defer(() => {
      if (!this.game) throw new Error('Load the wheel before spinning.');
      return from(this.client().play());
    }).pipe(
      tap(({ nextRoll, rollsAvailable }) =>
        this.spinState.set({ nextRoll, rollsAvailable }),
      ),
      map((response): Spin => ({
        uuid: crypto.randomUUID(),
        game: this.game!,
        createdTs: new Date(),
        segment: engineWinningSegment(this.game!, response),
        nextSpinInfo: {
          allowedToSpin: true,
          nextSpinInSeconds: 0,
          startTs: Date.now(),
          lastSpinTransaction: null,
        },
      })),
      catchError((error: unknown) => {
        if (error instanceof EngineAuthenticationError) this.setError(error);
        return throwError(() => error);
      }),
    );
  }

  dynamicWallets() {
    return defer(() => from(this.client().dynamicWallets())).pipe(
      catchError((error: unknown) => {
        if (error instanceof EngineAuthenticationError) this.setError(error);
        return throwError(() => error);
      }),
    );
  }
}
